/**
 * Tag di stato dell'attesa di risposta su una mail inviata.
 *
 * Quattro stati, con lo stesso linguaggio visivo dei tag etichetta (pallino +
 * testo in variante compatta, pill in variante piena) per non introdurre un
 * secondo vocabolario grafico nella stessa riga:
 *   in attesa -> neutro, mostra entro quando ci si aspetta la risposta
 *   scaduto   -> rosso, il termine e' passato senza risposta
 *   risposto  -> verde, l'attesa si e' chiusa da sola
 *   chiuso    -> silenziato, chiuso a mano
 */

import React from 'react';
import { Clock, AlertTriangle, CheckCircle2, BellOff } from 'lucide-react';
import type { MonitoraggioRef, StatoMonitoraggio } from '../lib/api';

interface Props {
  monitoraggio: MonitoraggioRef;
  /** Variante compatta per la riga della lista; piena nell'header del messaggio. */
  compatta?: boolean;
}

const ICONE = {
  in_attesa: Clock,
  scaduto: AlertTriangle,
  risposto: CheckCircle2,
  chiuso: BellOff,
} as const;

/** Classi Tailwind per stato: testo per la variante compatta, pill per quella piena. */
const STILI: Record<StatoMonitoraggio, { testo: string; pill: string; etichetta: string }> = {
  in_attesa: {
    testo: 'text-muted-foreground',
    pill: 'bg-muted text-muted-foreground',
    etichetta: 'In attesa di risposta',
  },
  scaduto: {
    testo: 'text-destructive',
    pill: 'bg-destructive/15 text-destructive',
    etichetta: 'Nessuna risposta',
  },
  risposto: {
    testo: 'text-emerald-600 dark:text-emerald-400',
    pill: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
    etichetta: 'Ha risposto',
  },
  chiuso: {
    testo: 'text-muted-foreground/70',
    pill: 'bg-muted text-muted-foreground/70',
    etichetta: 'Monitoraggio chiuso',
  },
};

function formattaData(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Testo esteso: allo stato aggiunge la scadenza, che e' l'informazione utile. */
export function descrizioneMonitoraggio(m: MonitoraggioRef): string {
  const stile = STILI[m.stato] ?? STILI.chiuso;
  const quando = formattaData(m.scadenza);
  if (!quando) return stile.etichetta;
  if (m.stato === 'in_attesa') return `${stile.etichetta} entro il ${quando}`;
  if (m.stato === 'scaduto') return `${stile.etichetta}: termine scaduto il ${quando}`;
  return stile.etichetta;
}

export function MonitoraggioChip({ monitoraggio, compatta = false }: Props) {
  const stile = STILI[monitoraggio.stato] ?? STILI.chiuso;
  const Icona = ICONE[monitoraggio.stato] ?? BellOff;
  const descrizione = descrizioneMonitoraggio(monitoraggio);

  if (compatta) {
    return (
      <span
        className={`inline-flex items-center gap-1 text-xs ${stile.testo}`}
        title={descrizione}
      >
        <Icona className="h-3 w-3 shrink-0" aria-hidden="true" />
        <span className="sr-only">{descrizione}</span>
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs ${stile.pill}`}
      title={descrizione}
    >
      <Icona className="h-3 w-3 shrink-0" aria-hidden="true" />
      {descrizione}
    </span>
  );
}
